import React, {useCallback, useEffect, useState} from "react";
import {Box} from "@mui/system";
import {
    Autocomplete,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import _ from "lodash";
import {organizationSettingsService} from "_services";
import {Auth} from "components/Authorization";
import {SnackBar} from "components/common";
import BasicModal from "../../../common/BasicModal";
import styles from "../organizationMainStyles";
import {fragmentId} from "../Utilities/organizationSettingsConstants";
import {getLabel} from "../Utilities/organizationSettingsUtils";
import NoDataView from "../EmptyStates/NoDataView";
import CreateTrendsAutoActionModal from "./CreateTrendsAutoActionModal";
import AutoActionsTable from "./AutoActionsTable";

function handleRequestError(error) {
    if (_.get(error, "response.status") === 401) {
        Auth.processLogout();
        return;
    }
    const message = _.get(error, "response.data.message") || _.get(error, "message") || "Something went wrong.";
    SnackBar.showMessage(message);
}

const paginationStyles = {
    bar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 1,
        pt: 1,
    },
    label: {
        fontFamily: "LarsseitMedium",
        fontSize: "14px",
        color: "#495D5E",
    },
};

const createAutoActionOptions = [
    {kind: "keyword", label: "Keyword-based"},
    {kind: "tag", label: "Tag-based"},
    {kind: "ml", label: "ML-based"},
    {kind: "companyAlreadyReplied", label: "Company already replied"},
];

const AUTO_ACTIONS_PAGE_SIZE = 10;

const secondaryButtonSx = {
    textTransform: "none",
    fontFamily: "LarsseitMedium",
    color: "#0C2728",
    borderColor: "#CED4D4",
};

export default function AutoActions({organizationId, organizationTrendsId}) {
    const label = getLabel(fragmentId.AUTO_ACTIONS);
    const sectionTitle = _.get(label, "AUTO_ACTIONS", "Auto Actions");

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [listRefreshNonce, setListRefreshNonce] = useState(0);
    const [deletingAutoActionId, setDeletingAutoActionId] = useState(null);
    const [deleteConfirmTarget, setDeleteConfirmTarget] = useState(null);
    const [createModal, setCreateModal] = useState({open: false, kind: null});
    const [createComboKey, setCreateComboKey] = useState(0);

    useEffect(() => {
        setPage(0);
    }, [organizationTrendsId]);

    const load = useCallback(async () => {
        if (_.isEmpty(organizationId) || organizationTrendsId == null || organizationTrendsId === "") {
            setRows([]);
            setHasNextPage(false);
            return;
        }
        setLoading(true);
        try {
            const res = await organizationSettingsService.listTrendsAutoActions({
                max: AUTO_ACTIONS_PAGE_SIZE + 1,
                offset: page * AUTO_ACTIONS_PAGE_SIZE,
                organizationId: organizationTrendsId,
            });
            const raw = Array.isArray(res.data) ? res.data : [];
            if (raw.length === 0 && page > 0) {
                setPage(page - 1);
                return;
            }
            const hasMore = raw.length > AUTO_ACTIONS_PAGE_SIZE;
            const data = raw.slice(0, AUTO_ACTIONS_PAGE_SIZE);
            setRows(data);
            setHasNextPage(hasMore);
        } catch (e) {
            setRows([]);
            setHasNextPage(false);
            handleRequestError(e);
        } finally {
            setLoading(false);
        }
    }, [organizationId, organizationTrendsId, page]);

    useEffect(() => {
        load();
    }, [load, listRefreshNonce]);

    const handlePrevPage = useCallback(() => {
        if (page > 0 && !loading) {
            setPage(page - 1);
        }
    }, [page, loading]);

    const handleNextPage = useCallback(() => {
        if (hasNextPage && !loading) {
            setPage(page + 1);
        }
    }, [hasNextPage, loading, page]);

    const handleListRefresh = useCallback(() => {
        setPage(0);
        setListRefreshNonce((n) => n + 1);
    }, []);

    const executeDeleteAutoAction = useCallback(
        async (id) => {
            if (id == null || id === "") {
                return false;
            }
            setDeletingAutoActionId(id);
            try {
                await organizationSettingsService.deleteTrendsAutoAction(id);
                SnackBar.showMessage("Auto action removed.");
                await load();
                return true;
            } catch (e) {
                handleRequestError(e);
                return false;
            } finally {
                setDeletingAutoActionId(null);
            }
        },
        [load]
    );

    const openDeleteAutoActionConfirm = useCallback((row) => {
        if (row?.id == null) {
            return;
        }
        setDeleteConfirmTarget({
            id: row.id,
            name: row.name != null && String(row.name).trim() ? String(row.name) : null,
            type: row.type != null && String(row.type).trim() ? String(row.type) : null,
        });
    }, []);

    const closeDeleteAutoActionConfirm = useCallback(() => {
        if (deletingAutoActionId == null) {
            setDeleteConfirmTarget(null);
        }
    }, [deletingAutoActionId]);

    const confirmDeleteAutoAction = useCallback(async () => {
        if (!deleteConfirmTarget?.id) {
            return;
        }
        const id = deleteConfirmTarget.id;
        const ok = await executeDeleteAutoAction(id);
        if (ok) {
            setDeleteConfirmTarget(null);
        }
    }, [deleteConfirmTarget, executeDeleteAutoAction]);

    const openCreateModal = useCallback((kind) => {
        setCreateModal({open: true, kind});
        setCreateComboKey((k) => k + 1);
    }, []);

    const closeCreateModal = useCallback(() => {
        setCreateModal({open: false, kind: null});
    }, []);

    if (_.isEmpty(organizationId)) {
        return <NoDataView />;
    }

    if (organizationTrendsId == null || organizationTrendsId === "") {
        return (
            <Box sx={styles.sectionWrapper}>
                <Box component="h2" sx={{...styles.heading, mb: 0}}>
                    {sectionTitle}
                </Box>
                <Divider orientation="horizontal" flexItem sx={styles.sectionDivider} />
                <Typography sx={styles.infoItem}>
                    This organization has no Trends ID; auto actions cannot be loaded.
                </Typography>
            </Box>
        );
    }

    const loadingBody = (
        <Box sx={styles.bodyLoading}>
            <CircularProgress sx={styles.bodyLoadingSpinner} />
        </Box>
    );

    return (
        <Box sx={styles.sectionWrapper}>
            <Box component="h2" sx={{...styles.heading, mb: 0}}>
                {sectionTitle}
            </Box>
            <Divider orientation="horizontal" flexItem sx={styles.sectionDivider} />
            <Box sx={{mb: 1.5, maxWidth: 420}}>
                <Autocomplete
                    key={createComboKey}
                    size="small"
                    options={createAutoActionOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(a, b) => a.kind === b.kind}
                    onChange={(event, option) => {
                        if (option?.kind) {
                            openCreateModal(option.kind);
                        }
                    }}
                    value={null}
                    ListboxProps={{
                        sx: {
                            py: 0.5,
                            "& .MuiAutocomplete-option": {
                                minHeight: 30,
                                py: 0.25,
                                px: 1.25,
                                fontSize: "0.8125rem",
                                fontFamily: "LarsseitRegular",
                                lineHeight: 1.2,
                            },
                        },
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            label="Create auto action"
                            placeholder="Choose type…"
                            InputProps={{
                                ...params.InputProps,
                                sx: {fontFamily: "LarsseitRegular"},
                            }}
                            InputLabelProps={{sx: {fontFamily: "LarsseitMedium", fontSize: "0.875rem"}}}
                        />
                    )}
                />
            </Box>
            <CreateTrendsAutoActionModal
                open={createModal.open && createModal.kind != null}
                onClose={closeCreateModal}
                actionKind={createModal.kind}
                organizationTrendsId={organizationTrendsId}
                onCreated={handleListRefresh}
            />
            <BasicModal
                open={deleteConfirmTarget != null}
                handleClose={closeDeleteAutoActionConfirm}
                title="Delete auto action?"
                style={{width: "440px", maxWidth: "calc(100vw - 32px)"}}
            >
                <Box sx={{display: "flex", flexDirection: "column", pt: 1}}>
                    <Typography sx={{fontFamily: "LarsseitRegular", fontSize: "0.875rem", color: "#0C2728"}}>
                        You are about to permanently delete an auto action.
                    </Typography>
                    <Box
                        sx={{
                            my: 2,
                            py: 1.25,
                            px: 1.5,
                            borderRadius: "5px",
                            border: "1px solid #EEEFEF",
                            bgcolor: "#F8F9F9",
                        }}
                    >
                        <Typography
                            component="div"
                            sx={{fontFamily: "LarsseitMedium", fontSize: "0.875rem", color: "#0C2728", lineHeight: 1.5}}
                        >
                            ID {deleteConfirmTarget?.id}
                            {deleteConfirmTarget?.name ? (
                                <>
                                    <br/>
                                    {deleteConfirmTarget.name}
                                </>
                            ) : null}
                            {deleteConfirmTarget?.type ? (
                                <>
                                    <br/>
                                    <Box component="span" sx={{fontFamily: "LarsseitRegular", color: "#495D5E"}}>
                                        {deleteConfirmTarget.type}
                                    </Box>
                                </>
                            ) : null}
                        </Typography>
                    </Box>
                    <Typography sx={{fontFamily: "LarsseitRegular", fontSize: "0.875rem", color: "#0C2728"}}>
                        This cannot be undone.
                    </Typography>
                    <Box sx={{display: "flex", justifyContent: "flex-end", gap: 1, mt: 2}}>
                        <Button
                            variant="outlined"
                            onClick={closeDeleteAutoActionConfirm}
                            disabled={deletingAutoActionId != null}
                            sx={secondaryButtonSx}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={confirmDeleteAutoAction}
                            disabled={deletingAutoActionId != null}
                            sx={{textTransform: "none", fontFamily: "LarsseitMedium"}}
                        >
                            {deletingAutoActionId != null ? "Deleting…" : "Delete"}
                        </Button>
                    </Box>
                </Box>
            </BasicModal>
            {loading ? (
                loadingBody
            ) : rows.length === 0 ? (
                <Typography sx={styles.infoItem}>No auto actions returned for this organization.</Typography>
            ) : (
                <>
                    <AutoActionsTable
                        rows={rows}
                        deletingAutoActionId={deletingAutoActionId}
                        onDelete={openDeleteAutoActionConfirm}
                    />
                    <Box sx={paginationStyles.bar}>
                        <IconButton
                            aria-label="Previous page"
                            size="small"
                            onClick={handlePrevPage}
                            disabled={page === 0 || loading}
                        >
                            <KeyboardArrowLeftIcon fontSize="small"/>
                        </IconButton>
                        <Typography sx={paginationStyles.label}>Page {page + 1}</Typography>
                        <IconButton
                            aria-label="Next page"
                            size="small"
                            onClick={handleNextPage}
                            disabled={!hasNextPage || loading}
                        >
                            <KeyboardArrowRightIcon fontSize="small"/>
                        </IconButton>
                    </Box>
                </>
            )}
        </Box>
    );
}
